import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  Input,
  Component,
  ElementRef,
  ContentChild,
  AfterViewInit,
  ChangeDetectionStrategy,
} from '@angular/core';

import { SnIconComponent } from '@lib/ui/icon';

import { SnBaseButtonComponent } from '../../base/base-button.component';
import { SnButtonLabelDirective } from '../../directives/button-label.directive';
import { SnFabButtonPosition } from '../../types/fab-button-position';

@Component({
  selector: 'sn-fab-button',
  templateUrl: './fab-button.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  inputs: ['disabled', 'color', 'tabIndex', 'type', 'isLoading', 'fluid'],
  host: {
    class: 'sn-fab-button',
    '[class.loading]': 'isLoadingState',
    '[attr.disabled]': 'isDisabledState',
  },
})
export class SnFabButtonComponent extends SnBaseButtonComponent implements AfterViewInit {
  @Input()
  public icon: string;

  @Input()
  public small: boolean;

  @Input()
  public label: string;

  @Input()
  public fluid: boolean;

  @Input()
  get extended(): boolean {
    return this._extended;
  }
  set extended(value: boolean) {
    this._extended = coerceBooleanProperty(value);
  }

  @Input()
  set position(value: SnFabButtonPosition) {
    if (this._position === value) {
      return;
    }

    if (this._position) {
      const positionClass = this._convertPosition(this._position);
      this.hostElement.classList.remove(`sn-fab_${positionClass}`, `ngx-mat-fab--${positionClass}`);
    }

    this._position = value;

    if (this._position) {
      const positionClass = this._convertPosition(this._position);
      this.hostElement.classList.add(`sn-fab_${positionClass}`, `ngx-mat-fab--${positionClass}`);
    }
  }

  @ContentChild(SnIconComponent, { read: ElementRef })
  private readonly _iconElementRef: ElementRef<HTMLElement>;

  @ContentChild(SnButtonLabelDirective, { read: ElementRef })
  private readonly _labelElementRef: ElementRef<HTMLElement>;

  private _position: SnFabButtonPosition;
  private _extended: boolean;

  constructor(elementRef: ElementRef<HTMLElement>) {
    super(elementRef);
    this.color = 'accent';
  }

  get isDisabledState(): boolean {
    return this.disabled || this.isLoading;
  }

  get isLoadingState(): boolean {
    return this.isLoading && !this.disabled;
  }

  get strokeWidth(): number {
    if (!this.extended) {
      return 4;
    }
  }

  get diameter(): number {
    return this.extended ? 24 : 56;
  }

  public ngAfterViewInit(): void {
    if (this._iconElementRef) {
      this._iconElementRef.nativeElement.classList.add('mat-fab__icon');
    }

    if (this._labelElementRef) {
      this._labelElementRef.nativeElement.classList.add('mat-fab__label');
    }
  }

  private _convertPosition(position: SnFabButtonPosition): string | null {
    switch (position) {
      case 'bottomLeft': {
        return 'bottom-left';
      }
      case 'bottomRight': {
        return 'bottom-right';
      }
      default: {
        return null;
      }
    }
  }
}
